import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';

import { formatPlanCreditsLabel } from '../../../modules/subscription/billing-subscription.models';
import { BillingCatalogApiService } from '../../../modules/subscription/services/billing-catalog-api.service';
import { BillingSubscriptionStateService } from '../../../modules/subscription/services/billing-subscription-state.service';

@Component({
  selector: 'app-free-plan-upgrade-banner',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (state.showUpgradeBanner()) {
      <div
        class="relative z-[70] flex w-full flex-wrap items-center justify-center gap-3 bg-primary px-4 py-2 text-white"
        role="alert"
      >
        <span class="text-center text-sm font-medium">
          @if (bannerMode() === 'trial') {
            Your
            <strong>{{ trialPlanLabel() }}</strong>
            free trial ends on
            <strong>{{ trialEndsLabel() }}</strong>.
            Upgrade now to keep full access and avoid switching to the Free plan with
            <strong>{{ freeCreditsLabel() }} AI credits/month</strong>.
          } @else {
            You're on the Free plan with
            <strong>{{ freeCreditsLabel() }} AI credits/month</strong>.
            Upgrade to unlock higher limits and more features.
          }
        </span>
        <a
          routerLink="/subscription"
          class="shrink-0 rounded border border-white px-3 py-1 text-sm font-medium transition-colors hover:bg-white/10"
        >
          Upgrade now
        </a>
      </div>
    }
  `,
})
export class FreePlanUpgradeBannerComponent {
  private readonly catalogApi = inject(BillingCatalogApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly state = inject(BillingSubscriptionStateService);
  private readonly freePlanCredits = signal('50');

  readonly bannerMode = computed(() => this.state.upgradeBannerMode());

  constructor() {
    this.catalogApi
      .getPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ plans }) => {
          const freePlan = plans.find(
            (plan) => plan.planCode.toLowerCase() === 'free',
          );
          if (freePlan) {
            this.freePlanCredits.set(freePlan.aiCreditsPerMonth);
          }
        },
      });
  }

  readonly freeCreditsLabel = computed(() =>
    formatPlanCreditsLabel(this.freePlanCredits()),
  );

  readonly trialPlanLabel = computed(() => {
    const planName = this.state.subscription()?.plan.planName?.trim() ?? 'paid';
    return `${planName.charAt(0).toUpperCase()}${planName.slice(1).toLowerCase()}`;
  });

  readonly trialEndsLabel = computed(() => {
    const trialEndsAt = this.state.subscription()?.trialEndsAt;
    if (!trialEndsAt) {
      return 'soon';
    }
    const date = new Date(trialEndsAt);
    if (Number.isNaN(date.getTime())) {
      return 'soon';
    }
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  });
}
